import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, HeartHandshake, Quote, Target, Telescope } from "lucide-react";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";
import { IMAGES } from "../lib/site";

const About = () => (
  <>
    <Seo
      title="About My Hoom — Real Estate Advisory Built on Trust"
      path="/about"
      description="My Hoom blends six years of investment platform experience with brand-led, concierge service. Mission: simpler, smarter, more rewarding real estate."
    />
    <section className="relative bg-navy text-white py-28 lg:py-36 overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <img src={IMAGES.advisory} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 to-navy" />
      </div>
      <div className="container-tight relative z-10 max-w-3xl">
        <Reveal>
          <span className="eyebrow text-gold">About My Hoom</span>
          <h1 className="mt-3 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05]">
            Built where <span className="text-gold">advisory</span> meets <span className="text-gold">experience</span>.
          </h1>
          <p className="mt-7 text-white/80 text-lg leading-relaxed">
            My Hoom was founded on a simple belief: real estate decisions deserve the same rigour as any other wealth decision — and the same warmth as a personal recommendation from someone you trust.
          </p>
        </Reveal>
      </div>
    </section>

    {/* Founder's vision */}
    <section className="py-24 lg:py-32">
      <div className="container-tight grid lg:grid-cols-12 gap-14 items-center">
        <Reveal className="lg:col-span-5">
          <div className="relative rounded-2xl overflow-hidden card-lift">
            <img src={IMAGES.apartment} alt="Founder vision" loading="lazy" className="w-full h-[520px] object-cover" />
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-7">
          <span className="eyebrow">Founder's vision</span>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-navy leading-tight">Six years of building. A lifetime of learning.</h2>
          <div className="mt-6 space-y-5 text-navy/70 text-lg leading-relaxed">
            <p>
              Over the past six years, we've built a real estate investment platform with one obsession — understanding how people actually behave when money, family, and a home are all on the line.
            </p>
            <p>
              That obsession became expertise. In market behaviour. In investment psychology. In the small, intentional design choices that turn a transaction into a relationship.
            </p>
            <p>
              My Hoom is the result — a real estate advisory shaped equally by brand-strategy thinking and on-the-ground market knowledge. A rare combination that lets us be both advisory and experience-led.
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    {/* Mission & Vision */}
    <section className="bg-[#FAFAFA] py-24 lg:py-32 border-y border-black/5">
      <div className="container-tight grid md:grid-cols-2 gap-8">
        <Reveal>
          <div className="card-lift p-10 border border-black/5 h-full">
            <Target className="text-gold" size={28} strokeWidth={1.5} />
            <h3 className="mt-5 font-serif text-3xl text-navy">Our Mission</h3>
            <p className="mt-4 text-navy/65 leading-relaxed">
              To empower homebuyers and investors with expert guidance, personalised solutions, and transparent processes — making real estate simpler, smarter, and more rewarding.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="card-lift p-10 border border-black/5 h-full">
            <Telescope className="text-teal" size={28} strokeWidth={1.5} />
            <h3 className="mt-5 font-serif text-3xl text-navy">Our Vision</h3>
            <p className="mt-4 text-navy/65 leading-relaxed">
              To become Bengaluru's most trusted and comprehensive real estate advisory platform for both living and investment needs — and to extend that promise globally, starting with Dubai.
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    {/* Quote */}
    <section className="py-24 lg:py-28 bg-navy text-white">
      <div className="container-tight max-w-3xl text-center">
        <Reveal>
          <Quote className="mx-auto text-gold" size={32} strokeWidth={1.5} />
          <p className="mt-6 font-serif text-3xl sm:text-4xl leading-snug">
            &ldquo;We don't sell square feet. We help people choose a place to live, a way to grow, and a future they can stand behind.&rdquo;
          </p>
          <div className="mt-7 text-sm tracking-[0.2em] uppercase text-gold">— My Hoom Founding Team</div>
        </Reveal>
      </div>
    </section>

    {/* Team / Culture */}
    <section className="py-24 lg:py-32">
      <div className="container-tight">
        <Reveal className="max-w-2xl">
          <span className="eyebrow">Team & Culture</span>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-navy">A small team. Genuinely senior.</h2>
          <p className="mt-5 text-navy/65 text-lg leading-relaxed">
            We are intentionally small — every advisor on the My Hoom desk has direct deal-making experience and stays accountable from first call to keys in hand.
          </p>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {[
            { name: "Advisory Desk", role: "Personalised home search", icon: Compass },
            { name: "Investment Desk", role: "Residential & CRE strategy", icon: Target },
            { name: "Client Success", role: "Post-purchase, possession & rentals", icon: HeartHandshake },
          ].map((m, i) => (
            <Reveal key={m.name} delay={i * 0.1}>
              <div className="card-lift p-8 border border-black/5 h-full">
                <div className="h-12 w-12 rounded-xl bg-gold/10 text-gold grid place-items-center">
                  <m.icon size={20} strokeWidth={1.6} />
                </div>
                <h3 className="mt-5 font-serif text-2xl text-navy">{m.name}</h3>
                <div className="mt-1 text-sm text-navy/55">{m.role}</div>
                <p className="mt-4 text-sm text-navy/65 leading-relaxed">
                  Senior advisors with multi-cycle experience across Bengaluru, NRI corridors, and Dubai's freehold market.
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-16 text-center">
            <Link to="/contact" className="btn-gold text-sm">
              Meet the team <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default About;
